import React, { useState, useEffect } from 'react';
import { useCalculator } from '../../context/CalculatorContext';
import { formatCurrency, shortenLargeNumber } from '../../utils/formatters';
import { 
  Play, 
  Pause, 
  RotateCcw, 
  SkipForward, 
  Download, 
  FileText, 
  CheckCircle, 
  ChevronDown,
  Zap,
  TrendingUp,
  DollarSign,
  Award,
  Target,
  BarChart3,
  Activity,
  RefreshCw
} from 'lucide-react';
import { motion } from 'framer-motion';
import { 
  generateCompoundInterestData, 
  generateAvalancheTimeline, 
  calculateCompoundInterestComparison,
  calculateInvestmentComparison,
  downloadCompoundInterestReport
} from '../../utils/compoundInterestUtils';
import { compoundMultiplierContent } from '../../data/compoundMultiplierContent';
import { getDetailedCertificateEvolution } from '../../utils/calculations/certificateEvolution';
import AnimatedNumberDisplay from '../ui/AnimatedNumberDisplay';

const CompoundInterestMultiplier: React.FC = () => {
  const { investment, results } = useCalculator();
  
  const [activeSection, setActiveSection] = useState<'intro' | 'simulation' | 'comparison' | 'benefits'>('intro');
  const [currentStep, setCurrentStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationSpeed, setAnimationSpeed] = useState(1);
  const [intervalId, setIntervalId] = useState<NodeJS.Timeout | null>(null);
  const [downloadStatus, setDownloadStatus] = useState<'idle' | 'downloading' | 'complete'>('idle');
  const [downloadFormat, setDownloadFormat] = useState<'html' | 'pdf'>('html');
  const [comparisonMode, setComparisonMode] = useState<'reinvestment' | 'cetes' | 'realEstate' | 'savings'>('reinvestment');
  
  if (!results) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-green-200 border-t-green-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Calculando Multiplicador™...</p>
          <p className="text-gray-500 text-sm mt-2">Procesando datos del interés compuesto</p>
        </div>
      </div>
    );
  }

  // Generate compound interest data
  const compoundData = generateCompoundInterestData(investment);
  const avalancheTimeline = generateAvalancheTimeline(
    investment.initialCertificates,
    results.certificatesSummary.totalCertificates,
    investment.years,
    results.yearlyData,
    investment.currencyFormat
  );
  
  // Get certificate evolution data
  const certificateEvolution = getDetailedCertificateEvolution(investment);
  
  // Calculate comparisons
  const compoundComparison = calculateCompoundInterestComparison(investment, results);
  const investmentComparison = calculateInvestmentComparison(investment, results);

  // Animation control functions
  const startAnimation = () => {
    setIsAnimating(true);
    const interval = setInterval(() => {
      setCurrentStep(prev => {
        const nextStep = prev + 1;
        if (nextStep >= avalancheTimeline.length) {
          setIsAnimating(false);
          return avalancheTimeline.length - 1;
        }
        return nextStep;
      });
    }, 1000 / animationSpeed);
    setIntervalId(interval);
  };

  const pauseAnimation = () => {
    setIsAnimating(false);
    if (intervalId) {
      clearInterval(intervalId);
      setIntervalId(null);
    }
  };

  const resetAnimation = () => {
    pauseAnimation();
    setCurrentStep(0);
  };

  const skipToEnd = () => {
    pauseAnimation();
    setCurrentStep(avalancheTimeline.length - 1);
  };

  const changeSpeed = (speed: number) => {
    setAnimationSpeed(speed);
    if (isAnimating) {
      pauseAnimation();
      setAnimationSpeed(speed);
      startAnimation();
    }
  };

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [intervalId]);

  const handleDownload = (format: 'html' | 'pdf' = downloadFormat) => {
    setDownloadStatus('downloading');
    setDownloadFormat(format);
    
    try {
      downloadCompoundInterestReport(investment, results, format);
      setTimeout(() => {
        setDownloadStatus('complete');
        setTimeout(() => setDownloadStatus('idle'), 2000);
      }, 1000);
    } catch (error) {
      console.error('Error downloading report:', error);
      setDownloadStatus('idle');
    }
  };

  const renderIntroSection = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="text-center space-y-6"
    >
      <div className="w-24 h-24 bg-gradient-to-br from-green-500 to-emerald-600 rounded-3xl flex items-center justify-center mx-auto mb-6">
        <Zap className="w-12 h-12 text-white" />
      </div>
      
      <div>
        <h2 className="text-3xl font-bold text-gray-900 mb-4">{compoundMultiplierContent.title}</h2>
        <p className="text-xl text-gray-600 mb-6">{compoundMultiplierContent.tagline}</p>
      </div>
      
      <div className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-2xl p-8 max-w-4xl mx-auto">
        <h3 className="text-2xl font-bold text-green-800 mb-4">Tu Revolución Financiera Personal</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-4xl font-bold text-green-700 mb-2">
              {investment.initialCertificates}
            </div>
            <div className="text-green-600">Certificados que compras</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-blue-700 mb-2">
              {results.certificatesSummary.totalCertificates}
            </div>
            <div className="text-blue-600">Certificados que tendrás</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-purple-700 mb-2">
              +{results.certificatesSummary.fromReinvestment}
            </div>
            <div className="text-purple-600">Certificados GRATIS</div>
          </div>
        </div>
      </div>
      
      <button
        onClick={() => setActiveSection('simulation')}
        className="px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-3 mx-auto"
      >
        <Play className="w-6 h-6" />
        <span>Ver Efecto Avalancha en Acción</span>
      </button>
    </motion.div>
  );

  const renderSimulationSection = () => {
    // Get current year data from results (1-based indexing)
    const currentYear = Math.min(currentStep + 1, investment.years);
    const currentYearData = results.yearlyData[currentYear - 1];
    const currentTimeline = avalancheTimeline[currentStep];
    
    // Use real data from results if available, otherwise use calculated values
    const currentMetrics = currentYearData ? {
      monthlyIncome: currentYearData.citrusIncome / 12,
      patrimony: currentYearData.citrusPatrimony,
      capitalGain: currentYearData.citrusPatrimony - (investment.initialCertificates * investment.certificateBasePrice)
    } : {
      monthlyIncome: 0,
      patrimony: investment.initialCertificates * investment.certificateBasePrice,
      capitalGain: 0
    };
    
    // Get certificate count from certificate evolution data
    const currentCertificateData = certificateEvolution[currentYear - 1];
    const currentCertificates = currentCertificateData ? currentCertificateData.totalCertificates : investment.initialCertificates;
    
    // Calculate comparison metrics based on selected mode
    const calculateComparisonMetrics = () => {
      const initialInvestment = investment.initialCertificates * investment.certificateBasePrice;
      
      if (comparisonMode === 'reinvestment') {
        // Without reinvestment - DYNAMIC with lemon price increase
        let certificatePrice = investment.certificateBasePrice;
        if (currentYear <= 5) {
          certificatePrice = investment.certificateBasePrice * Math.pow(1 + investment.appreciationRate / 100, currentYear);
        } else {
          const maxAppreciatedValue = investment.certificateBasePrice * Math.pow(1 + investment.appreciationRate / 100, 5);
          if (investment.increaseLemonPrice) {
            certificatePrice = maxAppreciatedValue * Math.pow(1 + investment.lemonPriceIncrease / 100, currentYear - 5);
          } else {
            certificatePrice = maxAppreciatedValue;
          }
        }
        
        // Calculate monthly income (only after year 4)
        let monthlyIncome = 0;
        if (currentYear > 4) {
          let currentLemonPrice = investment.averageSalePricePerKg;
          if (currentYear > 5 && investment.increaseLemonPrice) {
            currentLemonPrice = investment.averageSalePricePerKg * Math.pow(1 + investment.lemonPriceIncrease / 100, currentYear - 5);
          }
          monthlyIncome = (investment.initialCertificates * investment.averageProductionPerHectare * currentLemonPrice * 0.1 * 0.65) / 12;
        }
        
        const patrimony = investment.initialCertificates * certificatePrice;
        const capitalGain = patrimony - initialInvestment;
        
        return {
          monthlyIncome,
          patrimony,
          capitalGain,
          label: 'Sin Multiplicador™',
          description: 'Solo los certificados que compraste'
        };
      } else if (comparisonMode === 'cetes') {
        // CETES comparison
        const cetesValue = initialInvestment * Math.pow(1 + investment.cetesRate / 100, currentYear);
        const cetesAnnualIncome = cetesValue * (investment.cetesRate / 100);
        const cetesMonthlyIncome = cetesAnnualIncome / 12;
        const cetesCapitalGain = cetesValue - initialInvestment;
        
        return {
          monthlyIncome: cetesMonthlyIncome,
          patrimony: cetesValue,
          capitalGain: cetesCapitalGain,
          label: 'CETES',
          description: `${investment.cetesRate}% anual tradicional`
        };
      } else {
        // Real Estate comparison
        const realEstateValue = initialInvestment * Math.pow(1 + investment.realEstateAppreciation / 100, currentYear);
        const realEstateAnnualIncome = realEstateValue * (investment.realEstateRent / 100);
        const realEstateMonthlyIncome = realEstateAnnualIncome / 12;
        const realEstateCapitalGain = realEstateValue - initialInvestment;
        
        return {
          monthlyIncome: realEstateMonthlyIncome,
          patrimony: realEstateValue,
          capitalGain: realEstateCapitalGain,
          label: 'Bienes Raíces',
          description: `${investment.realEstateAppreciation}% apreciación + ${investment.realEstateRent}% renta`
        };
      }
    };
    
    const comparisonMetrics = calculateComparisonMetrics();
    
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-4"
      >
        <div className="text-center mb-4">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
            El Efecto Avalancha en Acción
          </h2>
          <p className="text-sm text-gray-600">
            Observa cómo se multiplican tus activos productivos año tras año
          </p>
        </div>

        {/* Comparison Mode Tabs */}
        <div className="flex justify-center mb-4">
          <div className="flex bg-white rounded-lg p-1 shadow-md">
            <button
              onClick={() => setComparisonMode('reinvestment')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                comparisonMode === 'reinvestment'
                  ? 'bg-green-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              vs Sin Reinversión
            </button>
            <button
              onClick={() => setComparisonMode('cetes')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                comparisonMode === 'cetes'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              vs CETES
            </button>
            <button
              onClick={() => setComparisonMode('realEstate')}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                comparisonMode === 'realEstate'
                  ? 'bg-orange-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              vs Bienes Raíces
            </button>
          </div>
        </div>

        {/* Reinvestment Impact Notice */}
        <div className="text-center mb-4">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg border border-blue-200">
            <RefreshCw className="w-4 h-4" />
            <span className="text-sm">
              {investment.reinvestProfits 
                ? "Con reinversión activada: Todas las inversiones reinvierten sus rendimientos"
                : "Sin reinversión: Todas las inversiones reciben rendimientos como ingreso mensual"
              }
            </span>
          </div>
        </div>

        {/* Single compact container */}
        <div className="bg-white rounded-xl shadow-lg p-3 max-h-[85vh] overflow-hidden">
          {/* Animation Controls - Ultra compact inline */}
          <div className="flex justify-center items-center gap-1 mb-2">
            <button
              onClick={isAnimating ? pauseAnimation : startAnimation}
             className="p-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
             {isAnimating ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            </button>
            
            {[0.5, 1, 2, 5].map(speed => (
              <button
                key={speed}
                onClick={() => changeSpeed(speed)}
               className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  animationSpeed === speed
                    ? 'bg-indigo-600 text-white'
                   : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {speed}x
              </button>
            ))}
            
           <button onClick={skipToEnd} className="p-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors">
             <SkipForward className="w-5 h-5" />
            </button>
            
           <button onClick={resetAnimation} className="p-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors">
             <RotateCcw className="w-5 h-5" />
            </button>
          </div>

          {/* Side-by-side comparison - Ultra compact */}
          <div className="grid grid-cols-2 gap-1 mb-2">
            {/* Comparison Investment */}
            <div className={`rounded p-2 ${
              comparisonMode === 'reinvestment' ? 'bg-gray-50' :
              comparisonMode === 'cetes' ? 'bg-blue-50' :
              'bg-orange-50'
            }`}>
              <div className="text-center">
                <div className={`text-xs font-semibold mb-1 ${
                  comparisonMode === 'reinvestment' ? 'text-gray-700' :
                  comparisonMode === 'cetes' ? 'text-blue-700' :
                  'text-orange-700'
                }`}>
                  {comparisonMetrics.label}
                </div>
                <div className={`text-sm font-bold mb-1 ${
                  comparisonMode === 'reinvestment' ? 'text-gray-700' :
                  comparisonMode === 'cetes' ? 'text-blue-700' :
                  'text-orange-700'
                }`}>
                  {comparisonMode === 'reinvestment' ? `${investment.initialCertificates} cert.` : 'Inversión única'}
                </div>
                <div className="space-y-1">
                  <div className="bg-white p-1 rounded">
                    <div className="text-xs text-gray-600">Ingreso</div>
                    <div className={`text-xs font-bold ${
                      comparisonMode === 'reinvestment' ? 'text-gray-700' :
                      comparisonMode === 'cetes' ? 'text-blue-700' :
                      'text-orange-700'
                    }`}>
                      {shortenLargeNumber(comparisonMetrics.monthlyIncome)}
                    </div>
                  </div>
                  <div className="bg-white p-1 rounded">
                    <div className="text-xs text-gray-600">Patrimonio</div>
                    <div className={`text-xs font-bold ${
                      comparisonMode === 'reinvestment' ? 'text-gray-700' :
                      comparisonMode === 'cetes' ? 'text-blue-700' :
                      'text-orange-700'
                    }`}>
                      {shortenLargeNumber(comparisonMetrics.patrimony)}
                    </div>
                  </div>
                </div>
                <div className="text-xs text-gray-500 mt-1">{comparisonMetrics.description}</div>
              </div>
            </div>

            {/* Con Multiplicador™ */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-100 rounded p-2 border border-green-200">
              <div className="text-center">
                <div className="text-xs font-semibold text-green-800 mb-1">Con Multiplicador™</div>
                <div className="text-sm font-bold text-green-700 mb-1">{currentCertificates} cert.</div>
                <div className="space-y-1">
                  <div className="bg-white p-1 rounded">
                    <div className="text-xs text-green-600">Ingreso</div>
                    <div className="text-xs font-bold text-green-700">
                      {shortenLargeNumber(currentMetrics.monthlyIncome)}
                    </div>
                  </div>
                  <div className="bg-white p-1 rounded">
                    <div className="text-xs text-green-600">Patrimonio</div>
                    <div className="text-xs font-bold text-green-700">
                      {shortenLargeNumber(currentMetrics.patrimony)}
                    </div>
                  </div>
                </div>
                <div className="text-xs text-green-500 mt-1">Activos reales productivos</div>
              </div>
            </div>
          </div>

          {/* Year Display - HUGE and prominent */}
          <div className="text-center mb-2 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg p-2 text-white">
            <div className="text-3xl md:text-4xl font-bold mb-1">Año {currentYear}</div>
            <div className="text-xs">
              {(() => {
                if (currentYear <= 4) return "Período de Maduración";
                if (currentYear === 5) return "¡Primera Producción!";
                return "Efecto Avalancha";
              })()}
            </div>
          </div>

          {/* Certificate Visualization - Ultra compact */}
          <div className="bg-gradient-to-br from-green-50 to-emerald-100 rounded p-2 mb-2">
            <div className="text-center mb-1">
              <div className="text-xs font-bold text-green-600">
                {currentCertificates} certificados = {(currentCertificates * 1000).toLocaleString()} m²
              </div>
            </div>
            
            {/* Visual certificates - Ultra compact */}
            <div className="flex justify-center mb-1 flex-wrap gap-0.5">
              {Array.from({ length: Math.min(currentCertificates, 12) }).map((_, i) => (
                <motion.div
                  key={i}
                  className={`w-6 h-6 rounded flex items-center justify-center text-white font-bold text-xs ${
                    i < investment.initialCertificates 
                      ? 'bg-green-500' 
                      : 'bg-blue-500'
                  }`}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: i * 0.05, duration: 0.3 }}
                >
                  {i + 1}
                </motion.div>
              ))}
              {currentCertificates > 12 && (
                <div className="w-6 h-6 bg-purple-500 rounded flex items-center justify-center text-white font-bold text-xs">
                  +{currentCertificates - 12}
                </div>
              )}
            </div>
            
            {/* Event description */}
            <div className="text-center">
              <div className="text-xs text-gray-700">{currentTimeline?.description || "Preparando..."}</div>
            </div>
          </div>

          {/* Progress bar */}
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs text-gray-700">{currentYear}/{investment.years}</span>
            <div className="flex-1 bg-gray-200 rounded-full h-1">
              <div
                className="bg-gradient-to-r from-blue-500 to-indigo-600 h-1 rounded-full transition-all duration-300"
                style={{ width: `${(currentYear / investment.years) * 100}%` }}
              />
            </div>
            <span className="text-xs text-gray-500">{((currentYear / investment.years) * 100).toFixed(0)}%</span>
          </div>

          {/* Advantage Multiplier - Ultra compact */}
          <div className="bg-gradient-to-r from-purple-500 to-indigo-600 rounded p-2 text-white text-center mb-2">
            <div className="text-xs font-bold mb-1">🏆 Ventaja Multiplicador™</div>
            <div className="grid grid-cols-3 gap-1 text-xs">
              <div>
                <div className="text-purple-100">
                  {comparisonMode === 'reinvestment' ? 'Certificados' : 'Patrimonio'}
                </div>
                <div className="font-bold">
                  {comparisonMode === 'reinvestment' 
                    ? (currentCertificates > investment.initialCertificates 
                        ? `${(currentCertificates / investment.initialCertificates).toFixed(1)}x`
                        : '1.0x')
                    : (comparisonMetrics.patrimony > 0 
                        ? `${(currentMetrics.patrimony / comparisonMetrics.patrimony).toFixed(1)}x`
                        : '1.0x')
                  }
                </div>
              </div>
              <div>
                <div className="text-purple-100">Ingresos</div>
                <div className="font-bold">
                  {comparisonMetrics.monthlyIncome > 0 
                    ? `${(currentMetrics.monthlyIncome / comparisonMetrics.monthlyIncome).toFixed(1)}x`
                    : currentMetrics.monthlyIncome > 0 ? '∞' : '0x'
                  }
                </div>
              </div>
              <div>
                <div className="text-purple-100">Activos</div>
                <div className="font-bold">
                  {comparisonMode === 'reinvestment' 
                    ? `${currentCertificates}:${investment.initialCertificates}`
                    : `${currentCertificates}:1`
                  }
                </div>
              </div>
            </div>
          </div>

          {/* Continue Button - Only show when animation completes */}
          {!isAnimating && currentYear >= investment.years && (
            <div className="text-center">
              <button
                onClick={() => setActiveSection('comparison')}
                className="px-3 py-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold rounded text-sm flex items-center gap-1 mx-auto"
              >
                <span>Ver Comparación Final</span>
                <ChevronDown className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>
      </motion.div>
    );
  };

  const renderComparisonSection = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Comparación Final</h2>
        <p className="text-gray-600">Resultados después de {investment.years} años de inversión</p>
      </div>

      {/* Comparison Mode Selector */}
      <div className="flex justify-center mb-6">
        <div className="flex bg-white rounded-lg p-1 shadow-md">
          <button
            onClick={() => setComparisonMode('reinvestment')}
            className={`px-4 py-2 rounded-md font-medium transition-colors ${
              comparisonMode === 'reinvestment'
                ? 'bg-green-600 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            vs Sin Reinversión
          </button>
          <button
            onClick={() => setComparisonMode('cetes')}
            className={`px-4 py-2 rounded-md font-medium transition-colors ${
              comparisonMode === 'cetes'
                ? 'bg-blue-600 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            vs CETES
          </button>
          <button
            onClick={() => setComparisonMode('realEstate')}
            className={`px-4 py-2 rounded-md font-medium transition-colors ${
              comparisonMode === 'realEstate'
                ? 'bg-orange-600 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            vs Bienes Raíces
          </button>
          <button
            onClick={() => setComparisonMode('savings')}
            className={`px-4 py-2 rounded-md font-medium transition-colors ${
              comparisonMode === 'savings'
                ? 'bg-purple-600 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            vs Ahorro
          </button>
        </div>
      </div>

      {/* Final Comparison Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-gradient-to-br from-green-50 to-emerald-100 p-6 rounded-xl border border-green-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-200 rounded-lg flex items-center justify-center">
              <Zap className="w-5 h-5 text-green-700" />
            </div>
            <h3 className="text-lg font-semibold text-green-800">Con Multiplicador™</h3>
          </div>
          
          <div className="space-y-4">
            <div className="flex justify-between">
              <span className="text-green-700">Certificados:</span>
              <span className="font-bold text-green-800">{compoundComparison.withReinvestment.certificates}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-green-700">Patrimonio:</span>
              <span className="font-bold text-green-800">
                {formatCurrency(compoundComparison.withReinvestment.patrimony, investment.currencyFormat)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-green-700">Ingreso Mensual:</span>
              <span className="font-bold text-green-800">
                {formatCurrency(compoundComparison.withReinvestment.monthlyIncome, investment.currencyFormat)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-green-700">Multiplicador:</span>
              <span className="font-bold text-green-800">{compoundComparison.withReinvestment.multiplier.toFixed(1)}x</span>
            </div>
          </div>
        </div>

        <div className={`p-6 rounded-xl border ${
          comparisonMode === 'reinvestment' ? 'bg-gray-50 border-gray-200' :
          comparisonMode === 'cetes' ? 'bg-blue-50 border-blue-200' :
          comparisonMode === 'realEstate' ? 'bg-orange-50 border-orange-200' :
          'bg-purple-50 border-purple-200'
        }`}>
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              comparisonMode === 'reinvestment' ? 'bg-gray-200' :
              comparisonMode === 'cetes' ? 'bg-blue-200' :
              comparisonMode === 'realEstate' ? 'bg-orange-200' :
              'bg-purple-200'
            }`}>
              <BarChart3 className={`w-5 h-5 ${
                comparisonMode === 'reinvestment' ? 'text-gray-700' :
                comparisonMode === 'cetes' ? 'text-blue-700' :
                comparisonMode === 'realEstate' ? 'text-orange-700' :
                'text-purple-700'
              }`} />
            </div>
            <h3 className={`text-lg font-semibold ${
              comparisonMode === 'reinvestment' ? 'text-gray-800' :
              comparisonMode === 'cetes' ? 'text-blue-800' :
              comparisonMode === 'realEstate' ? 'text-orange-800' :
              'text-purple-800'
            }`}>
              {comparisonMode === 'reinvestment' ? 'Sin Multiplicador™' :
               comparisonMode === 'cetes' ? 'CETES' :
               comparisonMode === 'realEstate' ? 'Bienes Raíces' :
               'Ahorro Tradicional'}
            </h3>
          </div>
          
          <div className="space-y-4">
            <div className="flex justify-between">
              <span className={`${
                comparisonMode === 'reinvestment' ? 'text-gray-700' :
                comparisonMode === 'cetes' ? 'text-blue-700' :
                comparisonMode === 'realEstate' ? 'text-orange-700' :
                'text-purple-700'
              }`}>
                {comparisonMode === 'reinvestment' ? 'Certificados:' : 'Activos:'}
              </span>
              <span className={`font-bold ${
                comparisonMode === 'reinvestment' ? 'text-gray-800' :
                comparisonMode === 'cetes' ? 'text-blue-800' :
                comparisonMode === 'realEstate' ? 'text-orange-800' :
                'text-purple-800'
              }`}>
                {comparisonMode === 'reinvestment' ? compoundComparison.withoutReinvestment.certificates :
                 comparisonMode === 'cetes' ? investmentComparison.cetes.certificates :
                 comparisonMode === 'realEstate' ? investmentComparison.realEstate.certificates :
                 investmentComparison.savings.certificates}
              </span>
            </div>
            <div className="flex justify-between">
              <span className={`${
                comparisonMode === 'reinvestment' ? 'text-gray-700' :
                comparisonMode === 'cetes' ? 'text-blue-700' :
                comparisonMode === 'realEstate' ? 'text-orange-700' :
                'text-purple-700'
              }`}>Patrimonio:</span>
              <span className={`font-bold ${
                comparisonMode === 'reinvestment' ? 'text-gray-800' :
                comparisonMode === 'cetes' ? 'text-blue-800' :
                comparisonMode === 'realEstate' ? 'text-orange-800' :
                'text-purple-800'
              }`}>
                {comparisonMode === 'reinvestment' ? formatCurrency(compoundComparison.withoutReinvestment.patrimony, investment.currencyFormat) :
                 comparisonMode === 'cetes' ? formatCurrency(investmentComparison.cetes.patrimony, investment.currencyFormat) :
                 comparisonMode === 'realEstate' ? formatCurrency(investmentComparison.realEstate.patrimony, investment.currencyFormat) :
                 formatCurrency(investmentComparison.savings.patrimony, investment.currencyFormat)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className={`${
                comparisonMode === 'reinvestment' ? 'text-gray-700' :
                comparisonMode === 'cetes' ? 'text-blue-700' :
                comparisonMode === 'realEstate' ? 'text-orange-700' :
                'text-purple-700'
              }`}>Ingreso Mensual:</span>
              <span className={`font-bold ${
                comparisonMode === 'reinvestment' ? 'text-gray-800' :
                comparisonMode === 'cetes' ? 'text-blue-800' :
                comparisonMode === 'realEstate' ? 'text-orange-800' :
                'text-purple-800'
              }`}>
                {comparisonMode === 'reinvestment' ? formatCurrency(compoundComparison.withoutReinvestment.monthlyIncome, investment.currencyFormat) :
                 comparisonMode === 'cetes' ? formatCurrency(investmentComparison.cetes.monthlyIncome, investment.currencyFormat) :
                 comparisonMode === 'realEstate' ? formatCurrency(investmentComparison.realEstate.monthlyIncome, investment.currencyFormat) :
                 formatCurrency(investmentComparison.savings.monthlyIncome, investment.currencyFormat)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className={`${
                comparisonMode === 'reinvestment' ? 'text-gray-700' :
                comparisonMode === 'cetes' ? 'text-blue-700' :
                comparisonMode === 'realEstate' ? 'text-orange-700' :
                'text-purple-700'
              }`}>Multiplicador:</span>
              <span className={`font-bold ${
                comparisonMode === 'reinvestment' ? 'text-gray-800' :
                comparisonMode === 'cetes' ? 'text-blue-800' :
                comparisonMode === 'realEstate' ? 'text-orange-800' :
                'text-purple-800'
              }`}>
                {comparisonMode === 'reinvestment' ? compoundComparison.withoutReinvestment.multiplier.toFixed(1) :
                 comparisonMode === 'cetes' ? investmentComparison.cetes.multiplier.toFixed(1) :
                 comparisonMode === 'realEstate' ? investmentComparison.realEstate.multiplier.toFixed(1) :
                 investmentComparison.savings.multiplier.toFixed(1)}x
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Multiplier Advantage */}
      <div className="bg-gradient-to-r from-purple-500 to-indigo-600 rounded-2xl p-6 text-white text-center">
        <h3 className="text-xl font-bold mb-4">🏆 Poder del Multiplicador™</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <div className="text-3xl font-bold mb-2">
              {comparisonMode === 'reinvestment' ? 
                (compoundComparison.withReinvestment.certificates / compoundComparison.withoutReinvestment.certificates).toFixed(1) :
                comparisonMode === 'cetes' ?
                (compoundComparison.withReinvestment.certificates / investmentComparison.cetes.certificates).toFixed(1) :
                comparisonMode === 'realEstate' ?
                (compoundComparison.withReinvestment.certificates / investmentComparison.realEstate.certificates).toFixed(1) :
                (compoundComparison.withReinvestment.certificates / investmentComparison.savings.certificates).toFixed(1)
              }x
            </div>
            <div className="text-purple-100">
              {comparisonMode === 'reinvestment' ? 'más certificados' : 'más activos'}
            </div>
          </div>
          <div>
            <div className="text-3xl font-bold mb-2">
              {comparisonMode === 'reinvestment' ? 
                (compoundComparison.withReinvestment.patrimony / compoundComparison.withoutReinvestment.patrimony).toFixed(1) :
                comparisonMode === 'cetes' ?
                (compoundComparison.withReinvestment.patrimony / investmentComparison.cetes.patrimony).toFixed(1) :
                comparisonMode === 'realEstate' ?
                (compoundComparison.withReinvestment.patrimony / investmentComparison.realEstate.patrimony).toFixed(1) :
                (compoundComparison.withReinvestment.patrimony / investmentComparison.savings.patrimony).toFixed(1)
              }x
            </div>
            <div className="text-purple-100">más patrimonio</div>
          </div>
          <div>
            <div className="text-3xl font-bold mb-2">
              {comparisonMode === 'reinvestment' ? 
                (compoundComparison.withReinvestment.monthlyIncome / compoundComparison.withoutReinvestment.monthlyIncome).toFixed(1) :
                comparisonMode === 'cetes' ?
                (compoundComparison.withReinvestment.monthlyIncome / investmentComparison.cetes.monthlyIncome).toFixed(1) :
                comparisonMode === 'realEstate' ?
                (compoundComparison.withReinvestment.monthlyIncome / investmentComparison.realEstate.monthlyIncome).toFixed(1) :
                (compoundComparison.withReinvestment.monthlyIncome / investmentComparison.savings.monthlyIncome).toFixed(1)
              }x
            </div>
            <div className="text-purple-100">más ingresos</div>
          </div>
        </div>
        
        <div className="mt-4 p-4 bg-white/20 backdrop-blur-sm rounded-xl">
          <p className="text-sm text-white/90">
            <strong>Comparando con:</strong> {
              comparisonMode === 'reinvestment' ? 'Cosecha Capital sin reinversión (solo certificados iniciales)' :
              comparisonMode === 'cetes' ? `CETES al ${investment.cetesRate}% anual` :
              comparisonMode === 'realEstate' ? `Bienes Raíces (${investment.realEstateAppreciation}% apreciación + ${investment.realEstateRent}% renta)` :
              `Ahorro Tradicional al ${investment.savingsRate}% anual`
            }
          </p>
        </div>
      </div>

      <div className="text-center">
        <button
          onClick={() => setActiveSection('benefits')}
          className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-2 mx-auto"
        >
          <span>Ver Beneficios Únicos</span>
          <ChevronDown className="w-5 h-5" />
        </button>
      </div>
    </motion.div>
  );

  const renderBenefitsSection = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Los 4 Pilares del Multiplicador™</h2>
        <p className="text-gray-600">Descubre qué hace único al Interés Compuesto Multiplicador™</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {compoundMultiplierContent.pillars.items.map((pillar, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-green-500"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center text-white font-bold text-xl">
                {pillar.number}
              </div>
              <h3 className="text-lg font-semibold text-gray-800">{pillar.title}</h3>
            </div>
            
            <p className="text-gray-600 mb-4">{pillar.description}</p>
            
            <ul className="space-y-2">
              {pillar.examples.map((example, exampleIndex) => (
                <li key={exampleIndex} className="flex items-start gap-2 text-sm text-gray-700">
                  <span className="text-green-600 mt-1">→</span>
                  <span>{example}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        ))}
      </div>

      {/* Investment Comparison */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-2xl p-6">
        <h3 className="text-xl font-semibold text-blue-800 mb-4">Comparativo con Otras Inversiones</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-xl shadow-sm border-l-4 border-green-500">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="w-5 h-5 text-green-600" />
              <h4 className="font-medium text-green-800">Cosecha Capital</h4>
            </div>
            <div className="text-2xl font-bold text-green-700 mb-1">
              {formatCurrency(investmentComparison.cosechaCapital.patrimony, investment.currencyFormat)}
            </div>
            <div className="text-sm text-green-600">{investmentComparison.cosechaCapital.multiplier.toFixed(1)}x multiplicador</div>
          </div>
          
          <div className="bg-white p-4 rounded-xl shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="w-5 h-5 text-blue-600" />
              <h4 className="font-medium text-blue-800">CETES</h4>
            </div>
            <div className="text-2xl font-bold text-blue-700 mb-1">
              {formatCurrency(investmentComparison.cetes.patrimony, investment.currencyFormat)}
            </div>
            <div className="text-sm text-blue-600">{investmentComparison.cetes.multiplier.toFixed(1)}x multiplicador</div>
          </div>
          
          <div className="bg-white p-4 rounded-xl shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-5 h-5 text-purple-600" />
              <h4 className="font-medium text-purple-800">Ahorro</h4>
            </div>
            <div className="text-2xl font-bold text-purple-700 mb-1">
              {formatCurrency(investmentComparison.savings.patrimony, investment.currencyFormat)}
            </div>
            <div className="text-sm text-purple-600">{investmentComparison.savings.multiplier.toFixed(1)}x multiplicador</div>
          </div>
          
          <div className="bg-white p-4 rounded-xl shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <Target className="w-5 h-5 text-orange-600" />
              <h4 className="font-medium text-orange-800">Bienes Raíces</h4>
            </div>
            <div className="text-2xl font-bold text-orange-700 mb-1">
              {formatCurrency(investmentComparison.realEstate.patrimony, investment.currencyFormat)}
            </div>
            <div className="text-sm text-orange-600">{investmentComparison.realEstate.multiplier.toFixed(1)}x multiplicador</div>
          </div>
        </div>
      </div>

      {/* Download Report */}
      <div className="text-center">
        <div className="flex justify-center gap-3">
          <button
            onClick={() => handleDownload('html')}
            disabled={downloadStatus === 'downloading'}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <Download className="w-5 h-5" />
            <span>Descargar HTML</span>
          </button>
          <button
            onClick={() => handleDownload('pdf')}
            disabled={downloadStatus === 'downloading'}
            className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
          >
            <Download className="w-5 h-5" />
            <span>Descargar PDF</span>
          </button>
        </div>
        
        {downloadStatus === 'downloading' && (
          <div className="mt-3 flex items-center justify-center gap-2 text-blue-600">
            <div className="w-4 h-4 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
            <span className="text-sm">Generando reporte...</span>
          </div>
        )}
        
        {downloadStatus === 'complete' && (
          <div className="mt-3 flex items-center justify-center gap-2 text-green-600">
            <CheckCircle className="w-4 h-4" />
            <span className="text-sm">¡Reporte descargado exitosamente!</span>
          </div>
        )}
      </div>
    </motion.div>
  );

  return (
    <div className="space-y-6">
      {/* Section Navigation */}
      <div className="flex justify-center">
        <div className="flex bg-white rounded-lg p-1 shadow-md">
          <button
            onClick={() => setActiveSection('intro')}
            className={`px-4 py-2 rounded-md font-medium transition-colors ${
              activeSection === 'intro'
                ? 'bg-green-600 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            Introducción
          </button>
          <button
            onClick={() => setActiveSection('simulation')}
            className={`px-4 py-2 rounded-md font-medium transition-colors ${
              activeSection === 'simulation'
                ? 'bg-indigo-600 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            Efecto Avalancha
          </button>
          <button
            onClick={() => setActiveSection('comparison')}
            className={`px-4 py-2 rounded-md font-medium transition-colors ${
              activeSection === 'comparison'
                ? 'bg-purple-600 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            Comparación
          </button>
          <button
            onClick={() => setActiveSection('benefits')}
            className={`px-4 py-2 rounded-md font-medium transition-colors ${
              activeSection === 'benefits'
                ? 'bg-blue-600 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            Beneficios
          </button>
        </div>
      </div>

      {/* Content based on active section */}
      {activeSection === 'intro' && renderIntroSection()}
      {activeSection === 'simulation' && renderSimulationSection()}
      {activeSection === 'comparison' && renderComparisonSection()}
      {activeSection === 'benefits' && renderBenefitsSection()}
    </div>
  );
};

export default CompoundInterestMultiplier;